import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Work } from "@/types/media";
import { TYPE_LABELS } from "@/types/media";

interface HeroCarouselProps {
  works: Work[];
}

export default function HeroCarousel({ works }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (works.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % works.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [works.length]);

  if (works.length === 0) return null;

  const go = (next: number) => {
    setIndex((next + works.length) % works.length);
  };

  return (
    <section className="relative h-[420px] w-full overflow-hidden bg-foreground-950 md:h-[560px]">
      {works.map((work, i) => (
        <Link
          key={work.id}
          to={`/detail/${work.id}`}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={work.backdrop}
            alt={work.title}
            title={`${work.title} 焦点推荐`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/10"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="mx-auto w-full px-4 md:px-6">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
                  <i className="ri-fire-fill"></i>
                  焦点推荐 · {TYPE_LABELS[work.type]}
                </span>
                <h2 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-5xl">
                  {work.title}
                </h2>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-background-100/90">
                  <span className="flex items-center gap-1 text-accent-400">
                    <i className="ri-star-fill"></i>
                    {work.rating.toFixed(1)}
                  </span>
                  <span>{work.year}</span>
                  <span>{work.region}</span>
                  <span>{work.genres.join(" · ")}</span>
                </div>
                <p className="mt-4 hidden max-w-xl text-sm leading-relaxed text-background-100/80 md:line-clamp-2 md:block">
                  {work.synopsis}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600">
                  <i className="ri-play-circle-line text-lg"></i>
                  查看详情
                </span>
              </div>
            </div>
          </div>
        </Link>
      ))}

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/40 md:left-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/40 md:right-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {works.map((work, i) => (
          <button
            key={work.id}
            type="button"
            aria-label={`切换到第${i + 1}张`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}